import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/navigation/AdminHeader';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import ProtectedRoute from '../../components/navigation/ProtectedRoute';
import FilterSidebar from './components/FilterSidebar';
import DriversTable from './components/DriversTable';
import AssignmentDetailPanel from './components/AssignmentDetailPanel';
import DriverFormModal from './components/DriverFormModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

const DriversManagementPortal = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [yards, setYards] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState({
    search: '',
    assignmentType: 'all',
    yardLocation: 'all',
    showActive: true,
    showInactive: false
  });

  const [drivers, setDrivers] = useState([]);

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    const userEmail = localStorage.getItem('userEmail');
    if (authToken && userEmail) {
      setUser({ email: userEmail });
    }
    fetchYards();
    fetchDrivers();
  }, []);

  const fetchYards = async () => {
    try {
      const { data, error } = await supabase?.from('yards')?.select('id, name')?.order('name');
      if (!error && data) {
        setYards(data);
      }
    } catch (err) {
      console.error('Error fetching yards:', err);
    }
  };

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('drivers')
        ?.select(`
          id, first_name, last_name, phone, email, yard_id, is_active,
          yards(name),
          driver_units!left(
            id, is_active,
            units(id, unit_number)
          )
        `)
        ?.order('last_name');
      if (!error && data) {
        const mapped = data?.map((d, idx) => {
          const activeAssignment = d?.driver_units?.find(du => du?.is_active !== false);
          const unitNumber = activeAssignment?.units?.unit_number || null;
          return {
            id: d?.id,
            driverId: `DRV-${String(idx + 1)?.padStart(3, '0')}`,
            name: `${d?.first_name || ''} ${d?.last_name || ''}`?.trim(),
            phone: d?.phone,
            email: d?.email || '',
            assignmentType: unitNumber ? 'Assigned' : 'Unassigned',
            currentAssignment: unitNumber,
            yardLocation: d?.yards?.name || '',
            yardLocationId: d?.yard_id ? String(d?.yard_id) : '',
            isActive: d?.is_active !== false
          };
        });
        setDrivers(mapped);
      }
    } catch (err) {
      console.error('Error fetching drivers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('sessionExpiry');
    localStorage.removeItem('userEmail');
    navigate('/admin-authentication-portal');
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      assignmentType: 'all',
      yardLocation: 'all',
      showActive: true,
      showInactive: false
    });
  };

  const handleSelectDriver = (driver) => {
    setSelectedDriver(driver);
  };

  const handleEditDriver = (driver) => {
    setEditingDriver(driver);
    setShowDriverForm(true);
  };

  const handleDeleteDriver = (driver) => {
    setDeletingDriver(driver);
  };

  const handleSaveDriver = async (driverData) => {
    try {
      const nameParts = driverData?.name?.split(' ') || [];
      const firstName = driverData?.firstName || nameParts?.[0] || '';
      const lastName = driverData?.lastName || nameParts?.slice(1)?.join(' ') || '';

      const dbData = {
        first_name: firstName,
        last_name: lastName,
        phone: driverData?.phone,
        email: driverData?.email || null,
        yard_id: driverData?.yardLocation || null,
        is_active: driverData?.isActive !== false
      };

      // Application-level duplicate check — controlled by VITE_ENFORCE_DRIVER_DUPLICATE_CHECK.
      // When true (production): block save if another driver shares the same phone OR email.
      // When false (testing): skip the check entirely and allow duplicate phone/email.
      const enforceCheck = import.meta.env?.VITE_ENFORCE_DRIVER_DUPLICATE_CHECK === 'true';

      if (enforceCheck) {
        // Build OR filter: match on phone, or on email if provided
        let orFilter = `phone.eq.${dbData?.phone}`;
        if (dbData?.email) {
          orFilter += `,email.eq.${dbData?.email}`;
        }

        const { data: existing, error: dupError } = await supabase
          ?.from('drivers')
          ?.select('id, phone, email')
          ?.or(orFilter);

        if (dupError) throw dupError;

        // Exclude the record being edited (if editing)
        const conflicts = existing?.filter(
          (d) => !editingDriver || d?.id !== editingDriver?.id
        );

        if (conflicts?.length > 0) {
          // Keep the modal open and show inline error — do NOT close the form
          setDuplicateError('Your entry duplicates an existing driver. Record not saved.');
          return;
        }
      }

      // Clear any previous duplicate error before saving
      setDuplicateError(null);

      if (editingDriver) {
        const { error } = await supabase?.from('drivers')?.update(dbData)?.eq('id', editingDriver?.id);
        if (error) throw error;
        showNotificationMessage('Driver updated successfully');
      } else {
        const { error } = await supabase?.from('drivers')?.insert([dbData]);
        if (error) throw error;
        showNotificationMessage('Driver added successfully');
      }
      await fetchDrivers();
      setShowDriverForm(false);
      setEditingDriver(null);
    } catch (err) {
      console.error('Error saving driver:', err);
      showNotificationMessage('Error saving driver. Please try again.');
      setShowDriverForm(false);
      setEditingDriver(null);
    }
  };

  const handleConfirmDelete = async (driverId) => {
    try {
      const { error } = await supabase?.from('drivers')?.delete()?.eq('id', driverId);
      if (error) throw error;
      await fetchDrivers();
      if (selectedDriver?.id === driverId) {
        setSelectedDriver(null);
      }
      showNotificationMessage('Driver deleted successfully');
    } catch (err) {
      console.error('Error deleting driver:', err);
      showNotificationMessage('Error deleting driver.');
    }
    setDeletingDriver(null);
  };

  const handleBulkAction = (action, driverIds) => {
    if (action === 'activate') {
      setDrivers((prev) => prev?.map((d) =>
        driverIds?.includes(d?.id) ? { ...d, isActive: true } : d
      ));
      showNotificationMessage(`${driverIds?.length} driver(s) activated`);
    } else if (action === 'deactivate') {
      setDrivers((prev) => prev?.map((d) =>
        driverIds?.includes(d?.id) ? { ...d, isActive: false } : d
      ));
      showNotificationMessage(`${driverIds?.length} driver(s) deactivated`);
    }
  };

  const handleCreateAssignment = (driver) => {
    showNotificationMessage(`Opening assignment creation for ${driver?.name}`);
  };

  const showNotificationMessage = (message) => {
    setNotificationMessage(message);
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const filteredDrivers = drivers?.filter((driver) => {
    if (filters?.search) {
      const query = filters?.search?.toLowerCase();
      if (
        !driver?.name?.toLowerCase()?.includes(query) &&
        !driver?.phone?.toLowerCase()?.includes(query) &&
        !driver?.email?.toLowerCase()?.includes(query)
      ) {
        return false;
      }
    }

    if (filters?.assignmentType !== 'all' && driver?.assignmentType !== filters?.assignmentType) {
      return false;
    }

    if (filters?.yardLocation !== 'all' && driver?.yardLocationId !== filters?.yardLocation) {
      return false;
    }

    if (!filters?.showActive && driver?.isActive) return false;
    if (!filters?.showInactive && !driver?.isActive) return false;

    return true;
  });

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-background">
        <AdminHeader user={user} onLogout={handleLogout} />
        <AdminNavigation />

        <div className="flex" style={{ marginTop: '7.5rem' }}>
          <FilterSidebar
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            yards={yards}
          />

          <main className="flex-1 p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-foreground">Drivers Management</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Showing {filteredDrivers?.length} of {drivers?.length} drivers
                </p>
              </div>
              <Button
                variant="default"
                iconName="UserPlus"
                iconPosition="left"
                onClick={() => {
                  setEditingDriver(null);
                  setShowDriverForm(true);
                }}
              >
                Add Driver
              </Button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Icon name="Loader2" size={32} strokeWidth={2} className="text-primary animate-spin" />
              </div>
            ) : (
              <DriversTable
                drivers={filteredDrivers}
                selectedDriverId={selectedDriver?.id}
                onSelectDriver={handleSelectDriver}
                onEdit={handleEditDriver}
                onDelete={handleDeleteDriver}
                onEditDriver={handleEditDriver}
                onDeleteDriver={handleDeleteDriver}
                onCreateAssignment={handleCreateAssignment}
                onBulkAction={handleBulkAction}
              />
            )}
          </main>

          {selectedDriver && (
            <AssignmentDetailPanel
              driver={selectedDriver}
              onClose={() => setSelectedDriver(null)}
              onCreateAssignment={handleCreateAssignment}
            />
          )}
        </div>

        {showDriverForm && (
          <DriverFormModal
            driver={editingDriver}
            onClose={() => {
              setShowDriverForm(false);
              setEditingDriver(null);
              setDuplicateError(null);
            }}
            onSave={handleSaveDriver}
            yards={yards}
            duplicateError={duplicateError}
            onClearDuplicateError={() => setDuplicateError(null)}
          />
        )}

        {deletingDriver && (
          <DeleteConfirmModal
            driver={deletingDriver}
            onClose={() => setDeletingDriver(null)}
            onConfirm={handleConfirmDelete}
          />
        )}

        {showNotification && (
          <div className="fixed bottom-4 right-4 z-50 bg-success text-success-foreground px-4 py-3 rounded-lg shadow-elevation-3 flex items-center space-x-2">
            <Icon name="CheckCircle" size={18} strokeWidth={2} />
            <span className="text-sm font-medium">{notificationMessage}</span>
          </div>
        )}
      </div>
    </ProtectedRoute>
  );
};

export default DriversManagementPortal;